import proj4 from 'proj4';
import * as turf from '@turf/turf';

export const parseCRS = (crsName) => {
  /**
   * Parse CRS name from various formats
   * @param {string} crsName - CRS name in various formats
   * @returns {string} Standardized EPSG code
   */
  if (crsName.includes("EPSG::")) {
    return `EPSG:${crsName.split("EPSG::")[1]}`;
  } else if (crsName.includes("urn:ogc:def:crs:OGC:1.3:CRS84")) {
    return "EPSG:4326";
  } else if (crsName.includes("urn:ogc:def:crs:EPSG::")) {
    return `EPSG:${crsName.split("urn:ogc:def:crs:EPSG::")[1]}`;
  }
  return crsName;
};

export const transformGeoJSON = (geojson, fromProj, toProj) => {
  /**
   * Transform GeoJSON coordinates from one projection to another
   * @param {Object} geojson - GeoJSON to transform (FeatureCollection, Feature, or Geometry)
   * @param {string} fromProj - Source projection system (e.g., "EPSG:26910", "EPSG:3857")
   * @param {string} toProj - Target projection system (e.g., "EPSG:4326" for WGS84)
   * @returns {Object} New GeoJSON object with coordinates transformed to the target projection
   * 
   * @note Automatically defines EPSG:26910 (UTM Zone 10N NAD83) projection for common use case
   * @note Creates a deep copy of the input to avoid mutating the original GeoJSON
   * @note Recursively transforms nested coordinate arrays for all geometry types
   */
  // Define EPSG:26910 (UTM Zone 10N NAD83)
  proj4.defs(
    "EPSG:26910",
    "+proj=utm +zone=10 +ellps=GRS80 +datum=NAD83 +units=m +no_defs"
  );

  const transformed = JSON.parse(JSON.stringify(geojson)); // Deep copy

  // Recursive function to transform coordinate arrays
  const transformCoords = (coords) => {
    if (Array.isArray(coords) && typeof coords[0] === "number") {
      // proj4 transforms [x, y] to [lng, lat] for WGS84
      return proj4(fromProj, toProj, coords);
    } else if (Array.isArray(coords)) {
      return coords.map(transformCoords);
    }
    return coords;
  };

  if (transformed.features) {
    transformed.features.forEach((feature) => {
      if (feature.geometry?.coordinates) {
        feature.geometry.coordinates = transformCoords(
          feature.geometry.coordinates
        );
      }
    });
  }

  return transformed;
};

export const simplifyGeoJSON = (geojson, tolerance = 0.001) => {
  /**
   * Simplify GeoJSON geometries for better rendering performance
   * Reduces the complexity of polygon/line geometries by removing coordinate points 
   * that don't significantly affect the visual appearance.
   * 
   * @param {Object} geojson - The GeoJSON object to simplify (Point, LineString, Polygon, etc.)
   * @param {number} [tolerance=0.001] - Distance threshold for simplification. 
   *   Default 0.001 (~111 meters at equator). Points closer than this distance 
   *   are considered redundant and may be removed. Higher values = more aggressive 
   *   simplification, lower values = more detail preserved.
   * @returns {Object} Simplified GeoJSON object, or original geojson if simplification fails
   * 
   * @note Uses Douglas-Peucker algorithm (highQuality: false) for faster processing
   * @note Includes error handling - returns original data if simplification fails
   */
  try {
    return turf.simplify(geojson, { tolerance, highQuality: false });
  } catch (error) {
    console.warn("Failed to simplify GeoJSON:", error);
    return geojson;
  }
};

export const processGeoJsonLayers = (rawGeoJson) => {
  /**
   * Process raw GeoJSON layers from API
   * Transforms projections and simplifies large datasets
   * 
   * @param {Object} rawGeoJson - Raw GeoJSON data from API (keyed by layer name)
   * @returns {Object} Processed GeoJSON layers ready for map rendering
   */
  const processedData = {};

  Object.entries(rawGeoJson).forEach(([key, layer]) => {
    if (layer?.type === "FeatureCollection" && layer.features?.length > 0) {
      let processedLayer = layer;

      // Transform to EPSG:4326 if needed
      if (layer.crs?.properties) {
        const sourceCRS = parseCRS(layer.crs.properties.name);

        if (sourceCRS !== "EPSG:4326" && !sourceCRS.includes("4326")) {
          console.log(`🔄 Transforming ${key} from ${sourceCRS} to EPSG:4326`);
          processedLayer = transformGeoJSON(layer, sourceCRS, "EPSG:4326");
        }
      }

      // Simplify geometry for large datasets
      if (processedLayer.features.length > 5000) {
        console.log(`✂️ Simplifying ${key} (${processedLayer.features.length} features)`);
        processedLayer = simplifyGeoJSON(processedLayer, 0.002);
      }

      processedData[key] = processedLayer;
    } else {
      console.warn(`⚠️ Skipping invalid layer: ${key}`, layer);
    }
  });

  return processedData;
};